import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { makeAuthenticatedRequest } from '../services/api';
import { FaDownload, FaSpinner, FaSync, FaEye } from 'react-icons/fa';

const ChartOfAccountsPage = () => {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Load accounts when component mounts
  useEffect(() => {
    loadAccountsOnMount();
  }, []);

  const loadAccountsOnMount = async () => {
    setRefreshing(true);
    await fetchAccounts();
    setRefreshing(false);
  };

  const handleRefreshAccounts = async () => {
    setRefreshing(true);
    setError(null);
    await fetchAccounts();
    setRefreshing(false);
  };

  const fetchAccounts = async () => {
    try {
      const response = await makeAuthenticatedRequest('/xero/accounts/local', {
        method: 'POST',
        body: {
          pageSize: 100, // Get more accounts initially
          orderBy: 'code',
          orderDirection: 'asc'
        }
      });

      console.log('Accounts from local storage:', response);

      // The makeAuthenticatedRequest returns responseData.data, so response is already the data portion
      if (response && response.accounts && Array.isArray(response.accounts)) {
        setAccounts(response.accounts);
        console.log('First 5 accounts from local storage:', response.accounts.slice(0, 5));
        return response.accounts;
      } else {
        console.error('Failed to fetch accounts from local storage:', response);
        setAccounts([]);
        return [];
      }
    } catch (err) {
      console.error('Error fetching accounts from local storage:', err);
      setAccounts([]);
      return [];
    }
  };

  const handleImportAccounts = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // First, import/refresh accounts from Xero
      const importResponse = await makeAuthenticatedRequest('/xero/accounts', {
        method: 'POST',
        body: {}
      });
      
      console.log('Import response from /xero/accounts:', importResponse);
      
      if (importResponse.success) {
        setSuccess(importResponse.message);
        
        // After successful import, fetch the accounts from local storage
        await fetchAccounts();
      } else {
        console.error('Failed to import accounts. Response:', importResponse);
        setError('Failed to import accounts from Xero');
      }
    } catch (err) {
      console.error('Error importing accounts:', err);
      setError(err.message || 'An error occurred while importing accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAccount = (account) => {
    setSelectedAccount(account);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAccount(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    if (status === 'ACTIVE') {
      return `${baseClasses} bg-green-100 text-green-800`;
    }
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  const getTypeBadge = (type) => {
    const baseClasses = "px-2 py-1 rounded text-xs font-medium";
    const typeColors = {
      'BANK': 'bg-blue-100 text-blue-800',
      'CURRENT': 'bg-purple-100 text-purple-800',
      'REVENUE': 'bg-green-100 text-green-800',
      'EXPENSE': 'bg-red-100 text-red-800',
      'ASSET': 'bg-yellow-100 text-yellow-800',
      'LIABILITY': 'bg-orange-100 text-orange-800',
      'EQUITY': 'bg-indigo-100 text-indigo-800'
    };
    return `${baseClasses} ${typeColors[type] || 'bg-gray-100 text-gray-800'}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Chart of Accounts</h1>
        <Link to="/" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
          Back to Dashboard
        </Link>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div />
        <div className="flex gap-3">
          <button
            onClick={handleRefreshAccounts}
            disabled={refreshing}
            className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 inline-flex items-center gap-2 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refreshing ? (
              <>
                <FaSpinner className="animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <FaSync />
                Refresh
              </>
            )}
          </button>
          <button
            onClick={handleImportAccounts}
            disabled={loading}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm inline-flex items-center gap-2 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <FaDownload />
                Import from Xero
              </>
            )}
          </button>
        </div>
      </div>

      <div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Account Count */}
          {accounts && accounts.length > 0 && (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
              Showing {accounts.length} accounts. Check the console for the first 5 accounts structure.
            </div>
          )}

          {/* Accounts Table */}
          {accounts && accounts.length > 0 && (
            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accounts.map((account) => (
                    <tr key={account.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {account.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={getTypeBadge(account.type)}>
                          {account.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.class}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={getStatusBadge(account.status)}>
                          {account.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleViewAccount(account)}
                          className="text-green-600 hover:text-gray-900"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}

          {/* Show message when no accounts are found */}
          {!refreshing && !loading && accounts && accounts.length === 0 && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              No accounts found. Try importing from Xero first.
            </div>
          )}

          {/* Placeholder for future account display */}
          {(!accounts || accounts.length === 0) && !refreshing && !loading && (
            <div className="text-center text-gray-500 py-12">
              <p>Chart of accounts will be displayed here once loaded.</p>
              <p className="text-sm mt-2">Click "Import from Xero" to get started or "Refresh" to load existing accounts.</p>
            </div>
          )}

          {/* Loading state for refresh */}
          {refreshing && (
            <div className="text-center text-gray-500 py-12">
              <FaSpinner className="animate-spin text-2xl mx-auto mb-4" />
              <p>Loading accounts...</p>
            </div>
          )}
      </div>

      {/* Account Details Modal */}
      {showModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Account Details</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code:</span>
                      <span className="font-medium">{selectedAccount.code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">{selectedAccount.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className={getTypeBadge(selectedAccount.type)}>
                        {selectedAccount.type}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Class:</span>
                      <span className="font-medium">{selectedAccount.class}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={getStatusBadge(selectedAccount.status)}>
                        {selectedAccount.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Description:</span>
                      <span className="font-medium">{selectedAccount.description || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Financial Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax Type:</span>
                      <span className="font-medium">{selectedAccount.taxType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Currency:</span>
                      <span className="font-medium">{selectedAccount.currencyCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reporting Code:</span>
                      <span className="font-medium">{selectedAccount.reportingCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reporting Name:</span>
                      <span className="font-medium">{selectedAccount.reportingCodeName}</span>
                    </div>
                    {selectedAccount.bankAccountNumber && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bank Account #:</span>
                        <span className="font-medium">{selectedAccount.bankAccountNumber}</span>
                      </div>
                    )}
                    {selectedAccount.bankAccountType && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bank Account Type:</span>
                        <span className="font-medium">{selectedAccount.bankAccountType}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Settings</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Enable Payments:</span>
                      <span className="font-medium">{selectedAccount.enablePaymentsToAccount ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Show in Expense Claims:</span>
                      <span className="font-medium">{selectedAccount.showInExpenseClaims ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Add to Watchlist:</span>
                      <span className="font-medium">{selectedAccount.addToWatchlist ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Has Attachments:</span>
                      <span className="font-medium">{selectedAccount.hasAttachments ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">System Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Account ID:</span>
                      <span className="font-mono text-xs">{selectedAccount.accountID}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created:</span>
                      <span className="font-medium">{formatDate(selectedAccount.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Updated:</span>
                      <span className="font-medium">{formatDate(selectedAccount.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeModal}
                  className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 transition duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccountsPage;
